import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { OverviewService } from './overview.service';
import { ApiService } from '../services/api.service';
import { Subject, takeUntil } from 'rxjs';
import { InitialsPipe } from '../pipe/initials.pipe';

interface Release {
  version: string;
  date: string;
  updates: string[];
}

interface OverviewData {
  summary: string;
  latestRelease: Release;
}

interface OverviewDataMap {
  [key: string]: OverviewData; // Index signature
}
@Component({
  selector: 'app-overview',
  imports: [CommonModule, InitialsPipe],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss'
})
export class OverviewComponent {
  private route = inject(ActivatedRoute);
  private api = inject(ApiService);
  private overviewService = inject(OverviewService);

  currentModule = '';
  currentProjectId = '';
  projectData: any = null;
  isLoading = false;

  private destroy$ = new Subject<void>();

  ngOnInit() {
    // Get module slug (like 'estater-admin')
    this.route.parent?.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.currentModule = params.get('module') || '';
      this.loadProjectIdAndOverview(this.currentModule);
    });
  }

  slugify(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  // 1️⃣ Fetch all projects and find matching one by slug
  loadProjectIdAndOverview(moduleSlug: string) {
    this.isLoading = true;

    this.api.get<any[]>('/projects').pipe(takeUntil(this.destroy$)).subscribe({
      next: (projects) => {
        const matched = projects.find(p => this.slugify(p.name) === moduleSlug);
        if (matched) {
          this.currentProjectId = matched._id;
          this.loadProjectOverview(this.currentProjectId);
        } else {
          console.warn('No project found for slug:', moduleSlug);
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('Failed to fetch projects', err);
        this.isLoading = false;
      }
    });
  }

  // 2️⃣ Fetch the overview details for that project
  loadProjectOverview(projectId: string) {
    this.overviewService.getProjectOverview(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.projectData = res;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to fetch project overview', err);
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
